import { z } from 'zod';

import {
	type CountryCode,
	countryCodeSchema,
	dateTimeSchema,
	emptyOrNullableDateOnlySchema,
	localDateTimeSchema,
} from '@apple/utils/globalization/models';
import type { Guid } from '@apple/utils/primitive';

import type { StoreId } from '@apple/features/store/models';

import {
	type StoreEventStatus,
	type StoreEventType,
	storeEventStatusSchema,
	storeEventTypeSchema,
} from './shared';

/** @see AppleRetail.Web.Areas.StoreEvents.Models.StoreEventModel */
export interface StoreEventModel {
	id: Guid;
	storeName: string;
	/** Maps to the store's third party id in the backend */
	storeNumber: StoreId;
	storeCountryCode: CountryCode;
	storeUtcOffsetMinutes: number;
	customerCode: string | null;
	transitDays: number | null;
	type: StoreEventType | null;
	status: StoreEventStatus | null;
	canBeCancelled: boolean;
	canBeSaved: boolean;
	canBeCompleted: boolean;
	canBeSubmitted: boolean;
	canBeArchived: boolean;
	canItemsBeAdded: boolean;
	hasErpDetails: boolean;
	isOpen: boolean;
	isInFinalState: boolean;
	isSubmitting: boolean;
	/** @remark This value is in utc */
	updatedDate: Date | string;
	/** @remark This value is in the store's timezone */
	eventDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	earlyManagementKitPickDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	earlyManagementKitShipDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	earlyManagementKitDeliveryDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	fullKitPickDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	fullKitShipDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	fullKitDeliveryDate: Date | string | null;
}

/** @see AppleRetail.Web.Features.StoreEvents.StoreEventManagementController.StoreEventDetailUpdateRequest */
export interface StoreEventUpdateModel {
	transitDays: number;
	type: StoreEventType;
	/** @remark This value is in the store's timezone */
	eventDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	earlyManagementKitPickDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	earlyManagementKitShipDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	earlyManagementKitDeliveryDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	fullKitPickDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	fullKitShipDate: Date | string | null;
	/** @remark This value is in the store's timezone */
	fullKitDeliveryDate: Date | string | null;
	comment: string | null;
}

export const DEFAULT_STORE_EVENT_UPDATE_MODEL = {
	transitDays: 0,
	type: 'NewStore',
	eventDate: null,
	earlyManagementKitPickDate: null,
	earlyManagementKitShipDate: null,
	earlyManagementKitDeliveryDate: null,
	fullKitPickDate: null,
	fullKitShipDate: null,
	fullKitDeliveryDate: null,
	comment: 'N/A',
} as const as StoreEventUpdateModel;

/** @see AppleRetail.Web.Areas.StoreEvents.Models.StoreEventModel */
export const storeEventModelSchema = z.object({
	id: z.string(),
	storeName: z.string(),
	storeNumber: z.string(),
	storeCountryCode: countryCodeSchema,
	storeUtcOffsetMinutes: z.number(),
	customerCode: z.string().nullable(),
	transitDays: z.number().nullable(),
	type: z
		.string()
		.transform(x => (x === '' ? null : x))
		.pipe(storeEventTypeSchema.nullable()),
	status: storeEventStatusSchema.nullable(),
	eventDate: localDateTimeSchema.nullable(),
	canBeCancelled: z.boolean(),
	canBeSaved: z.boolean(),
	canBeCompleted: z.boolean(),
	canBeSubmitted: z.boolean(),
	canBeArchived: z.boolean(),
	canItemsBeAdded: z.boolean(),
	hasErpDetails: z.boolean(),
	isOpen: z.boolean(),
	isInFinalState: z.boolean(),
	isSubmitting: z.boolean(),
	updatedDate: dateTimeSchema,
	earlyManagementKitPickDate: localDateTimeSchema.nullable(),
	earlyManagementKitShipDate: localDateTimeSchema.nullable(),
	earlyManagementKitDeliveryDate: localDateTimeSchema.nullable(),
	fullKitPickDate: localDateTimeSchema.nullable(),
	fullKitShipDate: localDateTimeSchema.nullable(),
	fullKitDeliveryDate: localDateTimeSchema.nullable(),
}) as z.ZodType<StoreEventModel>;

/** @see AppleRetail.Web.Features.StoreEvents.StoreEventManagementController.StoreEventDetailUpdateRequest */
export const storeEventUpdateModelSchema = z.object({
	transitDays: z.number().int().positive(),
	type: storeEventTypeSchema,
	eventDate: emptyOrNullableDateOnlySchema,
	earlyManagementKitPickDate: emptyOrNullableDateOnlySchema,
	earlyManagementKitShipDate: emptyOrNullableDateOnlySchema,
	earlyManagementKitDeliveryDate: emptyOrNullableDateOnlySchema,
	fullKitPickDate: emptyOrNullableDateOnlySchema,
	fullKitShipDate: emptyOrNullableDateOnlySchema,
	fullKitDeliveryDate: emptyOrNullableDateOnlySchema,
	comment: z.string().nullable(),
}) satisfies z.ZodType<StoreEventUpdateModel>;

export const storesByEventStatusResponseSchema = z.object({
	storeIds: z.array(z.string()),
	storeCodes: z.array(z.string()),
});
